<!--
📌 Componente: PraxisAssistentAvatar.vue
📂 Caminho: src/components/PraxisAssistentAvatar.vue
📝 Descrição: Exibe o corpo do PraxisAssistent e sobrepõe um rosto SVG
  animado ao visor conforme o estado do login.

👁️ Interface e Interação com o Usuário:
  - Mostra expressões visuais de espera, digitação, privacidade, carregamento,
    sucesso e erro.
  - Pisca em intervalos aleatórios quando o formulário está inativo.
  - Não recebe foco nem é anunciado por tecnologias assistivas.

📥 Entrada (Props):
  - `state` (String) - Estado visual atual do avatar.
  - `idLength` (Number) - Comprimento da Mitarbeiter-ID para o rastreamento
    horizontal dos olhos.

📤 Saída (Emits): Nenhuma.

🔗 Dependências:
  - Asset: @/assets/images/PraxisAssistent-body-512.webp
  - Vue: computed, getCurrentInstance, onMounted, onUnmounted, ref, watch
-->

<template>
  <div
    class="relative h-32 w-32 shrink-0 sm:h-36 sm:w-36 lg:h-40 lg:w-40"
    aria-hidden="true"
    role="presentation"
  >
    <img :src="assistantBody" alt="" class="h-full w-full object-contain" />

    <svg
      class="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
      viewBox="0 0 512 512"
      role="presentation"
    >
      <svg
        x="123"
        y="138"
        width="266"
        height="133"
        viewBox="0 0 200 100"
        role="presentation"
      >
        <defs>
          <pattern
            :id="gridPatternId"
            width="8"
            height="8"
            patternUnits="userSpaceOnUse"
          >
            <rect width="8" height="8" class="fill-azul-decorativo-claro" />
            <path d="M0 4h8M4 0v8" class="stroke-azul-suave" stroke-width="1" />
          </pattern>
          <filter
            :id="successGlowId"
            x="-25%"
            y="-25%"
            width="150%"
            height="150%"
          >
            <feDropShadow
              dx="0"
              dy="0"
              stdDeviation="3"
              flood-color="currentColor"
              flood-opacity="0.9"
              class="text-verde-suave"
            />
          </filter>
        </defs>

        <g
          :class="{
            'avatar-private-pulse': isPrivateMode,
            'avatar-success-pulse': isSuccess,
          }"
          :filter="isSuccess ? `url(#${successGlowId})` : undefined"
        >
          <rect
            x="4"
            y="5"
            width="192"
            height="90"
            rx="38"
            class="fill-cinza-900"
          />
          <rect
            v-if="isPrivateMode"
            x="8"
            y="9"
            width="184"
            height="82"
            rx="34"
            class="fill-cinza-900/40 stroke-cinza-900/70"
            stroke-width="5"
          />

          <g v-if="showsGridEyes" :transform="`translate(${pupilOffset} 0)`">
            <rect
              x="35"
              :y="eyeY"
              width="43"
              :height="eyeHeight"
              rx="16"
              :fill="`url(#${gridPatternId})`"
              class="stroke-azul-suave transition-transform duration-150 ease-out"
              stroke-width="2"
            />
            <rect
              x="122"
              :y="eyeY"
              width="43"
              :height="eyeHeight"
              rx="16"
              :fill="`url(#${gridPatternId})`"
              class="stroke-azul-suave transition-transform duration-150 ease-out"
              stroke-width="2"
            />
          </g>

          <g v-else-if="isPrivateMode" class="text-azul-suave">
            <path
              d="M37 42h40M123 42h40"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="6"
            />
            <circle cx="100" cy="72" r="8" class="fill-azul-suave" />
          </g>

          <g v-else-if="isShowPassword" class="text-azul-suave">
            <circle cx="61" cy="55" r="22" class="fill-azul-decorativo-claro" />
            <circle
              cx="147"
              cy="55"
              r="22"
              class="fill-azul-decorativo-claro"
            />
            <circle cx="67" cy="62" r="8" class="fill-cinza-900" />
            <circle cx="153" cy="62" r="8" class="fill-cinza-900" />
            <circle
              cx="100"
              cy="76"
              r="9"
              fill="none"
              stroke="currentColor"
              stroke-width="4"
            />
          </g>

          <g v-else-if="isLoading" class="fill-azul-decorativo-claro">
            <rect
              x="42"
              y="37"
              width="24"
              height="24"
              rx="4"
              class="avatar-loader-bar"
            />
            <rect
              x="88"
              y="29"
              width="24"
              height="40"
              rx="4"
              class="avatar-loader-bar avatar-loader-bar-delay-one"
            />
            <rect
              x="134"
              y="37"
              width="24"
              height="24"
              rx="4"
              class="avatar-loader-bar avatar-loader-bar-delay-two"
            />
            <path
              d="M78 78h44"
              class="stroke-azul-suave"
              stroke-linecap="round"
              stroke-width="4"
            />
          </g>

          <g v-else-if="isSuccess" class="text-azul-suave">
            <path
              d="M38 59c10-22 30-22 40 0M122 59c10-22 30-22 40 0"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-width="6"
            />
            <path
              d="M63 73c21 19 53 19 74 0"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-width="5"
            />
          </g>

          <g v-else-if="isError" class="stroke-vermelho-suave">
            <path
              d="M42 32l30 30m0-30L42 62M128 32l30 30m0-30l-30 30"
              stroke="currentColor"
              stroke-linecap="round"
              stroke-width="6"
            />
            <path
              d="M69 78c8-7 16 7 24 0s16 7 24 0"
              stroke="currentColor"
              fill="none"
              stroke-linecap="round"
              stroke-width="5"
            />
          </g>

          <path
            v-if="showsGridEyes && !isTypingId"
            d="M72 73c16 15 40 15 56 0"
            class="stroke-azul-suave"
            fill="none"
            stroke-linecap="round"
            stroke-width="4"
          />
          <path
            v-else-if="isTypingId"
            d="M76 77h48"
            class="stroke-azul-suave"
            stroke-linecap="round"
            stroke-width="4"
          />
        </g>
      </svg>
    </svg>
  </div>
</template>

<script setup>
import {
  computed,
  getCurrentInstance,
  onMounted,
  onUnmounted,
  ref,
  watch,
} from "vue";
import assistantBody from "@/assets/images/PraxisAssistent-body-512.webp";

const props = defineProps({
  state: {
    type: String,
    default: "idle",
    validator: (value) =>
      [
        "idle",
        "typing-id",
        "typing-password",
        "show-password",
        "loading",
        "success",
        "error",
      ].includes(value),
  },
  idLength: {
    type: Number,
    default: 0,
  },
});

const instance = getCurrentInstance();
const gridPatternId = `praxis-assistent-grid-${instance.uid}`;
const successGlowId = `praxis-assistent-success-${instance.uid}`;
const isBlinking = ref(false);
let blinkTimer;

const isTypingId = computed(() => props.state === "typing-id");
const isPrivateMode = computed(() => props.state === "typing-password");
const isShowPassword = computed(() => props.state === "show-password");
const isLoading = computed(() => props.state === "loading");
const isSuccess = computed(() => props.state === "success");
const isError = computed(() => props.state === "error");
const showsGridEyes = computed(
  () => props.state === "idle" || props.state === "typing-id"
);
const eyeY = computed(() =>
  isBlinking.value ? 46 : isTypingId.value ? 34 : 30
);
const eyeHeight = computed(() =>
  isBlinking.value ? 7 : isTypingId.value ? 31 : 38
);
const pupilOffset = computed(() => {
  const length = Math.min(Math.max(Number(props.idLength) || 0, 0), 9);
  return isTypingId.value ? length * 2 : 0;
});

function clearBlinkTimer() {
  if (blinkTimer) {
    clearTimeout(blinkTimer);
    blinkTimer = undefined;
  }
}

function scheduleBlink() {
  clearBlinkTimer();
  if (
    props.state !== "idle" ||
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return;
  }

  const delay = 3500 + Math.random() * 2000;
  blinkTimer = window.setTimeout(() => {
    isBlinking.value = true;
    blinkTimer = window.setTimeout(() => {
      isBlinking.value = false;
      scheduleBlink();
    }, 150);
  }, delay);
}

watch(
  () => props.state,
  () => {
    isBlinking.value = false;
    scheduleBlink();
  }
);

onMounted(scheduleBlink);
onUnmounted(clearBlinkTimer);
</script>

<style scoped>
.avatar-private-pulse {
  animation: avatar-private-pulse 1.2s ease-in-out infinite;
  transform-box: fill-box;
  transform-origin: center;
}

.avatar-loader-bar {
  animation: avatar-loader-bar 600ms ease-in-out infinite alternate;
  transform-box: fill-box;
  transform-origin: center;
}

.avatar-loader-bar-delay-one {
  animation-delay: 150ms;
}

.avatar-loader-bar-delay-two {
  animation-delay: 300ms;
}

.avatar-success-pulse {
  animation: avatar-success-pulse 500ms ease-out both;
  transform-box: fill-box;
  transform-origin: center;
}

@keyframes avatar-private-pulse {
  50% {
    opacity: 0.8;
    transform: scale(0.98);
  }
}

@keyframes avatar-loader-bar {
  to {
    opacity: 0.35;
    transform: scaleY(0.55);
  }
}

@keyframes avatar-success-pulse {
  50% {
    transform: scale(1.05);
  }
}

@media (prefers-reduced-motion: reduce) {
  .avatar-private-pulse,
  .avatar-loader-bar,
  .avatar-success-pulse {
    animation: none;
  }
}
</style>
